'use client';

import { useState } from 'react';

import { SystemPageType } from '@ravago/shared/page-data/enums/system-page-type';
import { FlatQuotationLine } from '@ravago/shared/page-data/models/elements';
import { Badge } from '@ravago/shared/radiance/components/badge/components/Badge/Badge';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Divider } from '@ravago/shared/radiance/components/divider/components/Divider/Divider';
import { Expand } from '@ravago/shared/radiance/components/expand/components/Expand/Expand';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { Link } from '@ravago/shared/radiance/components/link/components/Link/Link';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { usePathLocale } from '../../../hooks/use-path-locale';
import { SystemPageLink } from '../../content/system-page-link/system-page-link';
import { EstimatedDelivery } from './estimated-delivery';

interface Props {
   line: FlatQuotationLine;
   quoteDetailRoute?: string;
   selectedQuantity?: number;
   hasMtsLocal?: boolean;
   showValidUntil?: boolean;
}

export const QuoteLineSummary = ({ line, quoteDetailRoute, selectedQuantity, hasMtsLocal, showValidUntil }: Props) => {
   const translate = useTranslations();
   const format = useFormatter();
   const locale = usePathLocale();

   const [commentOpen, setCommentOpen] = useState(true);

   const quantityForSummary = selectedQuantity ?? line.lineQuantityAmount;
   const shouldShowPalletCount = !!line.linePalletSizeAmount && line.linePalletSizeAmount > 0;
   const palletCountRaw =
      shouldShowPalletCount && line.linePalletSizeAmount && line.linePalletSizeAmount > 0
         ? quantityForSummary / line.linePalletSizeAmount
         : undefined;
   const palletCount = palletCountRaw !== undefined && Number.isInteger(palletCountRaw) && palletCountRaw > 0 ? palletCountRaw : undefined;
   const palletSuffix = palletCount === 1 ? translate('dynamic-product-block.pallet') : translate('dynamic-product-block.pallets');
   const palletSummary = palletCount !== undefined ? `${palletCount} ${palletSuffix}` : undefined;
   const packagingSummary = [line.linePackaging, palletSummary].filter(Boolean).join(' | ');
   const showHeader = !!quoteDetailRoute || showValidUntil;

   return (
      <Box direction="column" gap="sm" backgroundColor="surface-2" spacing="md" border borderRadius="largest">
         <Box direction="column" background="white" spacing="md" gap="xs" borderRadius="largest" border>
            {showHeader && (
               <>
                  <Box justify="between" items="center">
                     {quoteDetailRoute && (
                        <Box>
                           <SystemPageLink
                              baseRoute={quoteDetailRoute}
                              type={SystemPageType.QUOTE_DETAIL}
                              locale={locale}
                              slug={line.quotationId}
                              target="_blank"
                              underline="always"
                           >
                              <Badge trailingIcon="arrow-up-right-from-square">{line.quotationName}</Badge>
                           </SystemPageLink>
                        </Box>
                     )}
                     {showValidUntil && (
                        <Typography color="subtle" variant="small">
                           {translate('extended-color-picker.valid-until')} {format.dateTime(new Date(line.validUntil))}
                        </Typography>
                     )}
                  </Box>

                  <Divider />
               </>
            )}

            <Box justify="between" items="start">
               <Box direction="column" gap="3xs">
                  <Typography variant="body-highlight">
                     {format.quantity({ amount: quantityForSummary, unitOfMeasure: line.lineQuantityUOM })}
                  </Typography>
                  <Typography variant="small" color="subtle">
                     {packagingSummary}
                  </Typography>
               </Box>
               <Box direction="column" gap="3xs" items="end">
                  <Typography variant="body-highlight">
                     {format.unitPrice(line.lineUnitPrice)}
                     {!line.isPriceFixed && '*'}
                  </Typography>
                  <Typography variant="small" color="subtle">
                     {translate('dynamic-product-block.total')}{' '}
                     {format.price({ amount: quantityForSummary * line.lineUnitPrice.price.amount, currency: line.lineUnitPrice.price.currency })}
                  </Typography>
               </Box>
            </Box>

            {!line.isPriceFixed && (
               <Typography variant="small" color="subtle">
                  *
                  {line.marketPricingMonth
                     ? translate('commodity-pricing.price-contingent-to-month-index', { month: format.fullMonthName(line.marketPricingMonth) })
                     : translate('commodity-pricing.to-be-confirmed')}
               </Typography>
            )}

            {(line.quoteDescription || line.lineRemark) && (
               <Box direction="column" backgroundColor="surface-1" spacing="sm" border borderRadius="inset">
                  <Expand
                     keepMounted
                     expanded={commentOpen}
                     onExpandToggle={setCommentOpen}
                     header={
                        <Box justify="between" items="center">
                           <Box gap="xs" items="center">
                              <Icon icon="comment" color="subtle" />
                              <Typography variant="body-highlight">{translate('dynamic-product-block.comment-left-by-seller')}</Typography>
                           </Box>
                           <Link>{commentOpen ? translate('dynamic-product-block.close') : translate('dynamic-product-block.view')}</Link>
                        </Box>
                     }
                  >
                     <Box direction="column" gap="sm" spacing={{ top: 'sm' }}>
                        {line.quoteDescription && (
                           <Box direction="column" gap="3xs">
                              <Typography variant="small-highlight">{translate('dynamic-product-block.header')}</Typography>
                              <Typography>{line.quoteDescription}</Typography>
                           </Box>
                        )}
                        {line.lineRemark && (
                           <Box direction="column" gap="3xs">
                              <Typography variant="small-highlight">{translate('dynamic-product-block.line')}</Typography>
                              <Typography>{line.lineRemark}</Typography>
                           </Box>
                        )}
                     </Box>
                  </Expand>
               </Box>
            )}
         </Box>
         {hasMtsLocal !== undefined && <EstimatedDelivery hasMtsLocal={hasMtsLocal} />}
      </Box>
   );
};
